import {
  ChangeDetectionStrategy,
  Component,
  Inject
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Dimensions, ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';
import Swal from '../../../../shared/utils/swal.util';

const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_PASSO = 0.1;

const JANELA_PADRAO = 260;
const JANELA_MINIMA = 80;

export const PERFIL_FOTO_TAMANHO = 320;

export interface PerfilCropResult {
  base64: string;
  arquivo: File;
}

export interface PerfilCropData {
  arquivo: File;
  tamanho?: number;
}

@Component({
  selector: 'app-perfil-crop',
  standalone: false,
  templateUrl: './perfil-crop.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './perfil-crop.component.css'
})
export class PerfilCropComponent {
  readonly zoomMin = ZOOM_MIN;
  readonly zoomMax = ZOOM_MAX;
  readonly zoomPasso = ZOOM_PASSO;

  readonly arquivo: File;
  readonly tamanhoFoto: number;

  /** Diâmetro, em pixels de tela, da janela circular fixa sobre a foto. */
  janelaRecorte = JANELA_PADRAO;
  zoom = ZOOM_MIN;
  transform: ImageTransform = { scale: ZOOM_MIN };
  previa: string | null = null;

  private blobCortado: Blob | null = null;

  constructor(
    public readonly dialogRef: MatDialogRef<PerfilCropComponent>,
    @Inject(MAT_DIALOG_DATA) dados: File | PerfilCropData
  ) {
    const entrada: PerfilCropData = dados instanceof File ? { arquivo: dados } : dados;

    this.arquivo = entrada.arquivo;
    this.tamanhoFoto = entrada.tamanho ?? PERFIL_FOTO_TAMANHO;
  }

  get podeConfirmar(): boolean {
    return this.previa !== null;
  }

  /**
   * A janela precisa caber no menor lado da foto exibida, senão o recorte
   * vira uma elipse em imagens pequenas.
   */
  aoPrepararRecorte(dimensoes: Dimensions): void {
    const limite = Math.floor(Math.min(dimensoes.width, dimensoes.height));

    if (limite < JANELA_MINIMA) {
      return;
    }

    this.janelaRecorte = Math.min(JANELA_PADRAO, limite);
  }

  onImageCropped(event: ImageCroppedEvent): void {
    this.previa = event.base64 ?? null;
    this.blobCortado = event.blob ?? null;
  }

  /** Mantém o deslocamento feito com o arraste ao aplicar o zoom depois. */
  aoMoverFoto(transform: ImageTransform): void {
    this.transform = { ...transform };
    this.zoom = this.limitarZoom(transform.scale ?? ZOOM_MIN);
  }

  aoFalharCarregamento(): void {
    Swal.fire({
      icon: 'error',
      title: 'Não foi possível abrir a imagem',
      text: 'Envie outro arquivo e tente novamente.'
    });

    this.dialogRef.close();
  }

  onZoomChange(valor: number): void {
    this.zoom = this.limitarZoom(valor);
    this.aplicarZoom();
  }

  aumentarZoom(): void {
    this.onZoomChange(this.zoom + ZOOM_PASSO);
  }

  diminuirZoom(): void {
    this.onZoomChange(this.zoom - ZOOM_PASSO);
  }

  reposicionar(): void {
    this.zoom = ZOOM_MIN;
    this.transform = { scale: ZOOM_MIN, translateH: 0, translateV: 0 };
  }

  confirmar(): void {
    if (!this.previa) {
      return;
    }

    const blob = this.blobCortado ?? this.base64ParaBlob(this.previa);
    const arquivo = new File([blob], this.nomeArquivoRecorte(), { type: 'image/png' });

    const resultado: PerfilCropResult = {
      base64: this.previa,
      arquivo
    };

    this.dialogRef.close(resultado);
  }

  private aplicarZoom(): void {
    this.transform = { ...this.transform, scale: this.zoom };
  }

  private nomeArquivoRecorte(): string {
    const base = this.arquivo.name.replace(/\.[^./\\]+$/, '');
    return `${base || 'foto-perfil'}.png`;
  }

  private base64ParaBlob(base64: string): Blob {
    const conteudo = base64.includes(',') ? base64.split(',')[1] : base64;
    const bytes = atob(conteudo);
    const buffer = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
      buffer[i] = bytes.charCodeAt(i);
    }
    return new Blob([buffer], { type: 'image/png' });
  }

  private limitarZoom(valor: number): number {
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number(valor)));
  }
}
